import { Link } from "react-router-dom";
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users,
  Trophy,
  Calendar,
  Image,
  TrendingUp,
  ArrowRight,
  Upload,
  Zap,
  Play,
  Download,
  DatabaseBackup,
} from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useStore } from "@/store/useStore";

export function Dashboard() {
  const { teams, matches, leagues, generatedCount, hydrate } = useStore();
  const importInputRef = useRef(null);
  const [pendingImport, setPendingImport] = useState(null); // { json, counts }
  const [importMode, setImportMode] = useState("merge"); // "merge" (additive) | "replace" (wipe)
  const [importing, setImporting] = useState(false);

  const handleExport = async () => {
    try {
      const { data } = await api.get("/data/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url; a.download = `streamthumb-dataset-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Dataset exported", {
        description: `${data.teams.length} teams · ${data.leagues.length} leagues · ${data.matches.length} matches · ${data.brands.length} brands · ${(data.media||[]).length} media`,
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    }
  };

  const onPickImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      if (!Array.isArray(json.teams) || !Array.isArray(json.leagues)) {
        throw new Error("Not a valid StreamThumb dataset");
      }
      setPendingImport({
        json,
        counts: {
          teams: json.teams.length,
          leagues: json.leagues.length,
          matches: (json.matches || []).length,
          brands: (json.brands || []).length,
          media: (json.media || []).length,
        },
      });
    } catch (err) {
      toast.error("Could not read file: " + (err?.message || "invalid JSON"));
    } finally {
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const performImport = async () => {
    if (!pendingImport) return;
    setImporting(true);
    try {
      const { data: result } = await api.post(`/data/import?mode=${importMode}`, pendingImport.json);
      await hydrate();
      const c = pendingImport.counts;
      const verb = importMode === "merge" ? "Merged" : "Replaced with";
      toast.success(`Dataset ${importMode === "merge" ? "merged" : "imported"}`, {
        description: `${verb} ${c.teams} teams · ${c.leagues} leagues · ${c.matches} matches · ${c.brands} brands · ${c.media} media. (Server reports: ${JSON.stringify(result?.counts || result)})`.slice(0, 200),
      });
      setPendingImport(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const stats = [
    {
      label: "Teams Added",
      value: teams.length,
      icon: Users,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      label: "Leagues",
      value: leagues.length,
      icon: Trophy,
      color: "text-accent",
      bgColor: "bg-accent/10",
    },
    {
      label: "Scheduled Matches",
      value: matches.length,
      icon: Calendar,
      color: "text-success",
      bgColor: "bg-success/10",
    },
    {
      label: "Thumbnails Generated",
      value: generatedCount,
      icon: Image,
      color: "text-warning",
      bgColor: "bg-warning/10",
    },
  ];

  const quickActions = [
    {
      title: "Add Teams",
      description: "Upload team logos and info",
      icon: Users,
      path: "/teams",
      color: "from-primary/20 to-primary/5",
    },
    {
      title: "Add Leagues",
      description: "Manage competitions & logos",
      icon: Trophy,
      path: "/leagues",
      color: "from-accent/20 to-accent/5",
    },
    {
      title: "Import Matches",
      description: "Upload your match CSV",
      icon: Upload,
      path: "/matches",
      color: "from-success/20 to-success/5",
    },
    {
      title: "Generate Thumbnails",
      description: "Create YouTube & UScreen thumbs",
      icon: Zap,
      path: "/generator",
      color: "from-warning/20 to-warning/5",
    },
  ];

  return (
    <div className="space-y-6 animate-in">
      {/* Hero Section */}
      <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-card via-secondary to-card border border-border">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-accent/20" />
        </div>
        <div className="relative p-6 lg:p-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3">
              <div className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium">
                NLS+ Thumbnail Generator
              </div>
            </div>
            <h1 className="text-3xl lg:text-4xl font-display font-bold text-foreground mb-3">
              Welcome to{" "}
              <span className="text-gradient">StreamThumb</span>
            </h1>
            <p className="text-muted-foreground text-base lg:text-lg mb-6 max-w-xl">
              Generate professional thumbnails for your sports livestreams.
              Upload teams, leagues, import matches, and create stunning visuals in
              seconds.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/generator">
                <Button variant="premium" size="lg" className="gap-2">
                  <Play className="w-4 h-4" />
                  Start Generating
                </Button>
              </Link>
              <Link to="/leagues">
                <Button variant="outline" size="lg" className="gap-2">
                  <Trophy className="w-4 h-4" />
                  Manage Leagues
                </Button>
              </Link>
              <Button
                variant="outline" size="lg" className="gap-2"
                onClick={handleExport}
                data-testid="export-dataset-button"
                title="Download a JSON snapshot of your full dataset (teams, leagues, matches, brands)"
              >
                <Download className="w-4 h-4" />
                Export Dataset
              </Button>
              <input
                ref={importInputRef} type="file" accept="application/json,.json"
                className="hidden" onChange={onPickImport}
                data-testid="import-dataset-input"
              />
              <Button
                variant="outline" size="lg" className="gap-2"
                onClick={() => {
                  // Defensive: clear value so re-picking the same filename re-fires onChange.
                  if (importInputRef.current) importInputRef.current.value = "";
                  importInputRef.current?.click();
                }}
                data-testid="import-dataset-button"
                title="Restore a JSON snapshot — useful for moving from preview to production"
              >
                <DatabaseBackup className="w-4 h-4" />
                Import Dataset
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm import dialog */}
      <AlertDialog open={!!pendingImport} onOpenChange={(open) => { if (!open) { setPendingImport(null); setImportMode("merge"); } }}>
        <AlertDialogContent data-testid="import-confirm-dialog" className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Import dataset</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <span className="block">
                  Snapshot contents:
                  {pendingImport && (
                    <span className="block mt-1 text-xs text-muted-foreground">
                      · {pendingImport.counts.teams} teams · {pendingImport.counts.leagues} leagues
                      · {pendingImport.counts.matches} matches · {pendingImport.counts.brands} brands
                      · {pendingImport.counts.media} media
                    </span>
                  )}
                </span>

                {/* Mode picker */}
                <div className="space-y-2 pt-2">
                  <span className="block text-xs font-medium text-foreground">How should it be applied?</span>

                  <label className="flex items-start gap-2 p-2.5 rounded-md border border-border cursor-pointer hover:bg-secondary/50"
                    data-testid="import-mode-merge-label">
                    <input
                      type="radio" name="import-mode" value="merge"
                      checked={importMode === "merge"}
                      onChange={() => setImportMode("merge")}
                      className="mt-0.5"
                      data-testid="import-mode-merge"
                    />
                    <span className="block">
                      <span className="block text-sm font-medium text-foreground">Merge (additive — recommended)</span>
                      <span className="block text-[11px] text-muted-foreground">
                        Upserts each record by id. New teams/leagues/matches/brands/media are added;
                        records that already exist with the same id are updated. Anything not in the snapshot is left alone.
                      </span>
                    </span>
                  </label>

                  <label className="flex items-start gap-2 p-2.5 rounded-md border border-border cursor-pointer hover:bg-secondary/50"
                    data-testid="import-mode-replace-label">
                    <input
                      type="radio" name="import-mode" value="replace"
                      checked={importMode === "replace"}
                      onChange={() => setImportMode("replace")}
                      className="mt-0.5"
                      data-testid="import-mode-replace"
                    />
                    <span className="block">
                      <span className="block text-sm font-medium text-destructive">Replace (wipe & restore)</span>
                      <span className="block text-[11px] text-muted-foreground">
                        Deletes ALL existing teams, leagues, matches, brands and media first, then inserts the snapshot.
                        Use only when the snapshot is the full intended state.
                      </span>
                    </span>
                  </label>
                </div>

                <span className="block text-[11px] text-muted-foreground">
                  Tip: Export your existing data first if you want a safety net.
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={importing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performImport}
              disabled={importing}
              data-testid="import-confirm-action"
              className={importMode === "replace"
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : ""}
            >
              {importing
                ? "Importing..."
                : (importMode === "merge" ? "Merge & Import" : "Replace & Import")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="glass-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {stat.label}
                    </p>
                    <p className="text-3xl font-display font-bold text-foreground">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-2.5 rounded-xl ${stat.bgColor}`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-display font-semibold text-foreground mb-4">
          Quick Actions
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Link key={action.title} to={action.path}>
                <Card className="glass-card h-full hover:border-primary/30 transition-all duration-200 group cursor-pointer">
                  <CardContent className="p-5 flex flex-col h-full">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-4`}
                    >
                      <Icon className="w-6 h-6 text-foreground" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground mb-1">
                      {action.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4 flex-1">
                      {action.description}
                    </p>
                    <div className="flex items-center text-primary text-sm font-medium group-hover:gap-2 transition-all">
                      Get Started
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Matches */}
      {matches.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-display font-semibold text-foreground">
              Upcoming Matches
            </h2>
            <Link to="/matches">
              <Button variant="ghost" size="sm" className="gap-1">
                View All
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {matches.slice(0, 3).map((match) => (
              <MatchCard key={match.id} match={match} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MatchCard({ match }) {
  const { teams, leagues } = useStore();
  const team1 = teams.find((t) => t.id === match.team1Id);
  const team2 = teams.find((t) => t.id === match.team2Id);
  const league = leagues.find((l) => l.id === match.leagueId);

  return (
    <Card className="glass-card overflow-hidden">
      {/* Mini Thumbnail Preview */}
      <div
        className="h-24 relative"
        style={{
          background: `linear-gradient(90deg, ${team1?.primaryColor || league?.primaryColor || '#1e3a5f'} 0%, ${team2?.primaryColor || league?.secondaryColor || '#0ea5e9'} 100%)`,
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden">
            {team1?.logo ? (
              <img src={mediaUrl(team1.logo)} alt="" className="w-full h-full object-contain p-1"  loading="lazy" decoding="async"/>
            ) : (
              <span className="text-white font-bold">{match.team1Name?.[0] || "T"}</span>
            )}
          </div>
          {league?.logo && (
            <div className="w-8 h-8 rounded bg-white/30 backdrop-blur-sm flex items-center justify-center overflow-hidden">
              <img src={mediaUrl(league.logo)} alt="" className="w-full h-full object-contain p-0.5"  loading="lazy" decoding="async"/>
            </div>
          )}
          <div className="w-12 h-12 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden">
            {team2?.logo ? (
              <img src={mediaUrl(team2.logo)} alt="" className="w-full h-full object-contain p-1"  loading="lazy" decoding="async"/>
            ) : (
              <span className="text-white font-bold">{match.team2Name?.[0] || "T"}</span>
            )}
          </div>
        </div>
      </div>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="sport-badge">{match.sport}</span>
          <span className="text-xs text-muted-foreground">
            {new Date(match.date).toLocaleDateString()}
          </span>
        </div>
        <p className="text-sm font-medium text-foreground">
          {team1?.name || match.team1Name || "TBD"} vs {team2?.name || match.team2Name || "TBD"}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {match.competition} • {match.time}
        </p>
      </CardContent>
    </Card>
  );
}
